import axios from 'axios';
import { AuthState } from '@okta/okta-auth-js';

const getApiUrl = () => process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const getHeaders = (authState: AuthState | null) => {
  const token = authState?.accessToken?.accessToken;
  if (!token) {
    throw new Error('Auth state or token is not available.');
  }
  return { Authorization: `Bearer ${token}` };
};

export interface RepoPassRate {
  repo: string;
  passRate: number;
  passed: number;
  total: number;
  blocked: number;
  runtimeErrors: number;
  lastRun: string;
  window: { start: string; end: string; };
}

export interface SustainedFailure {
  repo: string;
  consecutiveFailures: number;
  firstFailureDate: string;
  lastFailureDate: string;
  lastRunUrl: string;
}

export interface AggregateMetrics {
  overall: RepoPassRate;
  byPlatform: { github: RepoPassRate; ado: RepoPassRate };
  byMode: { enhanced: RepoPassRate; basic: RepoPassRate };
}

export interface BelowThresholdResponse {
  threshold: number;
  window_days: number;
  count: number;
  repos: RepoPassRate[];
}

export interface SustainedFailuresResponse {
  threshold: number;
  count: number;
  failures: SustainedFailure[];
}

const ciMetricsService = {
  getAggregate: async (authState: AuthState | null, days: number = 14): Promise<AggregateMetrics> => {
    const response = await axios.get<AggregateMetrics>(`${getApiUrl()}/api/ci-metrics/aggregate`, {
      headers: getHeaders(authState),
      params: { days }
    });
    return response.data;
  },

  getBelowThreshold: async (authState: AuthState | null, threshold: number = 80, days: number = 14): Promise<BelowThresholdResponse> => {
    const response = await axios.get<BelowThresholdResponse>(`${getApiUrl()}/api/ci-metrics/below-threshold`, {
      headers: getHeaders(authState),
      params: { threshold, days }
    });
    return response.data;
  },

  getSustainedFailures: async (authState: AuthState | null, threshold: number = 3): Promise<SustainedFailuresResponse> => {
    const response = await axios.get<SustainedFailuresResponse>(`${getApiUrl()}/api/ci-metrics/sustained-failures`, {
      headers: getHeaders(authState),
      params: { threshold }
    });
    return response.data;
  },

  exportCsv: async (authState: AuthState | null, days: number = 14) => {
    const response = await axios.get<BlobPart>(`${getApiUrl()}/api/ci-metrics/export`, {
      headers: getHeaders(authState),
      params: { format: 'csv', days },
      responseType: 'blob',
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ci-metrics-export-${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
};

export default ciMetricsService;